import * as React from 'react';
import { useRef, useMemo, Popper } from 'react';

/** Material UI Packages. */
import { Autocomplete, TextField } from '@mui/material';

import useEnhancedEffect from '@mui/material/utils/useEnhancedEffect';

const CustomAutocompleteCell = ({ params, options, value }) => {
    const inputRef = useRef(null);

    const handleChange = (event, newValue) => {
        params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue ? newValue.id : null,
        });
    };

    useEnhancedEffect(() => {
        if (params.hasFocus && inputRef.current) {
            const input = inputRef.current.querySelector(`input`);
            input?.focus();
        }
    }, [params.hasFocus, value]);

    const getOptionLabel = (option) => {
        if (typeof option === 'string') return option;
        return option?.name || '';
    };

    const isOptionEqualToValue = (option, val) => {
        if (!option || !val) return false;
        return option.id === val.id || option.id === val;
    };

    const selectedOption = useMemo(() => {
        if (!value) return null;
        if (typeof value === 'object') return value;
        return options.find((opt) => opt.id === value) || null;
    }, [value, options]);

    return (
        <>
            <Autocomplete
                ref={inputRef}
                value={selectedOption}
                options={options}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                onChange={handleChange}
                disableClearable
                renderInput={(inputParams) => (
                    <TextField
                        {...inputParams}
                        variant="standard"
                        sx={{
                            px: 0.3,
                            '& .MuiInputBase-root': {
                                border: 'none',
                            },
                        }}
                    />
                )}
                disablePortal={false}
                fullWidth
                ListboxProps={{
                    sx: {
                        fontSize: 11,
                    },
                }}
                sx={{
                    height: '100%',
                    '& .MuiInputBase-input': {
                        fontSize: '11px',
                    },
                    '& .MuiInputBase-root': {
                        p: 0,
                        height: '100%',
                    },
                    '& .MuiFormControl-root': {
                        height: '100%',
                    },
                }}
            />
        </>
    );
};

const handleTabKeyDown = (apiRef, editMode) => {
    if (!apiRef.current) return;
    return (params, event) => {
        // Custom tab key event handling.
        if (event.key === 'Tab') {
            const currentRowId = params.id;
            const currentField = params.field;
            const direction = event.shiftKey ? -1 : 1;

            // Get the currently visible columns in the data grid.
            const visibleColumns = apiRef.current.getVisibleColumns();
            // Current column index of the focused cell (excludes hidden columns).
            const currentColIndex = visibleColumns.findIndex((col) => col.field === currentField);

            const nextColIndex = currentColIndex + direction;

            const currentRowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(currentRowId);

            // Move focus to the next cell if the column index is within valid bounds; otherwise, retain default behavior.
            if (nextColIndex >= 0 && nextColIndex < visibleColumns.length) {
                event.preventDefault();
                const nextField = visibleColumns[nextColIndex].field;

                // Scroll to the cell to keep it in view when focused.
                apiRef.current.scrollToIndexes({ currentRowIndex, colIndex: nextColIndex });
                apiRef.current.setCellFocus(currentRowId, nextField);
            } else {
                const allDetailExpandedId = apiRef.current.getExpandedDetailPanels();
                const nextRowIndex = currentRowIndex + direction;
                // Get the current row's total row count.
                const totalRowNum = apiRef.current.getRowsCount();
                const allRowIds = apiRef.current.getAllRowIds();
                const nextRowId = allRowIds[nextRowIndex];
                const isDetailOpen = allDetailExpandedId.includes(currentRowId);

                let colIndex, nextField;

                // Move focus to the next cell if the row index is within valid bounds; otherwise, ratain default behavioir.
                if (nextRowIndex >= 0 && nextRowIndex < totalRowNum && !isDetailOpen) {
                    event.preventDefault();

                    if (direction === 1) {
                        colIndex = 0;
                        nextField = visibleColumns[colIndex].field;
                    } else if (direction === -1) {
                        colIndex = visibleColumns.length - 1;
                        nextField = visibleColumns[colIndex].field;
                    }

                    apiRef.current.scrollToIndexes({ nextRowIndex, colIndex });
                    apiRef.current.setCellFocus(nextRowId, nextField);
                }
            }
        }
        // Custom enter key event handling.
        if (event.key === 'Enter') {
            // If the popper exists in the component, assume autocomplete is open.
            const autocompleteDom = document.querySelector('.MuiAutocomplete-popper');
            const autocompleteOpen = autocompleteDom !== null ? true : false;

            const currentMode = params.cellMode;

            if (autocompleteOpen) {
                event.preventDefault();
                event.stopPropagation();
            } else if (currentMode === 'edit') {
                event.preventDefault();
                event.stopPropagation();

                if (editMode === 'row') {
                    apiRef.current.stopRowEditMode({ id: params.id, field: params.field });
                } else apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
            }
        }
    };
};

export { CustomAutocompleteCell, handleTabKeyDown };
